import { useMemo } from 'react';
import { css } from '@emotion/react';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { ContentLoader, Heading, Text, useModalControl } from '@frontend/design-system';
import { Divider } from '../../../../../../../../components/divider';
import { DATETIME_FORMATS } from '../../../../../../../../constants';
import { ScheduleRequestActionCalledFrom } from '../../../hooks/use-schedule-request-actions';
import { useGetScheduleRequest } from '../../../hooks/useGetScheduleRequest';
import { ScheduleRequestDetailsSpinnerLoader } from '../../components/ScheduleRequestDetailsSpinnerLoader';
import { AdditionalData } from './additional-data';
import { Footer } from './footer';
import { getPlatformLabel, getSourceLabel } from './helpers';
import { PatientTypeStatusBadge } from './patient-type-status-badge';
import { PreferredContactStatusBadge } from './preferred-contact-status-badge';
import { PreferredDays } from './preferred-days';
import { PreferredTimeStatusBadge } from './preferred-time-status-badge';
import { Row } from './row';
import { Section } from './section';

type Props = {
  locationId: string;
  patientContactedAt?: string;
  requestVersion: string;
  scheduleRequestId: string;
  dismissRequestModalProps: ReturnType<typeof useModalControl>;
  onDismissRequestIntent: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onMessage: (updateStatus: boolean, actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onPhoneCall: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onCompleteRequest: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
};

export const PartnerRequestItemDetails = ({
  locationId,
  patientContactedAt,
  requestVersion,
  scheduleRequestId,
  dismissRequestModalProps,
  onDismissRequestIntent,
  onMessage,
  onPhoneCall,
  onCompleteRequest,
}: Props) => {
  const { t } = useTranslation('schedule-request');

  // Fetch request details
  const requestQuery = useGetScheduleRequest(scheduleRequestId, requestVersion, locationId);

  const {
    isExistingUser,
    birthDate,
    phoneNumber,
    email,
    note,
    preferredContact,
    preferredDays,
    preferredTime,
    insuranceCarrier,
    insuranceGroupNumber,
    insuranceSubscriberName,
    insuranceSubscriberId,
    platform,
    source,
    campaignName,
    additionalData,
  } = useMemo(() => {
    const requestData = requestQuery.data;
    const schedulee = requestData?.schedulee;
    const insurance = schedulee?.insurance;
    const preferences = schedulee?.preferences;
    const attribution = requestData?.attribution;

    return {
      isExistingUser: !!schedulee?.existingUser,
      birthDate: schedulee && formatDate(schedulee.birthDate, DATETIME_FORMATS.US_DATE_LONG),
      phoneNumber: schedulee?.phoneNumber ? formatPhoneNumber(schedulee.phoneNumber) : '',
      email: schedulee?.email ?? '',
      note: requestData?.note ?? '',
      preferredContact: preferences?.preferredContact ?? '',
      preferredDays: preferences?.preferredDays ?? [],
      preferredTime: preferences?.preferredTime ?? '',
      insuranceCarrier: insurance?.carrier ?? '',
      insuranceGroupNumber: insurance?.groupNumber ?? '',
      insuranceSubscriberName: insurance?.subscriberName ?? '',
      insuranceSubscriberId: insurance?.subscriberId ?? '',
      platform: getPlatformLabel(attribution?.platform),
      source: getSourceLabel(attribution?.source),
      campaignName: attribution?.campaignName ?? '',
      additionalData: requestData?.additionalData,
    };
  }, [requestQuery.data]);

  if (requestQuery?.isLoading) {
    return <ScheduleRequestDetailsSpinnerLoader />;
  }

  if (requestQuery?.error) {
    return (
      <div css={{ height: 400 }}>
        <Text color='error' size='medium'>
          {t('Error fetching request details.')}
        </Text>
      </div>
    );
  }

  return (
    <div css={containerStyles}>
      <ContentLoader show={requestQuery.isLoading} />
      <Heading level={3}>{t('Request Data')}</Heading>

      <Divider />

      <Section heading={t('Patient')}>
        <Row title={t('Patient Type')} content={<PatientTypeStatusBadge isExistingUser={isExistingUser} />} />
        <Row title={t('Comments')} content={note} />
        <Row title={t('Birthdate')} content={birthDate} />
        <Row title={t('Email')} content={email} />
        <Row title={t('Phone')} content={phoneNumber} />
      </Section>

      <Divider />

      <Section heading={t('Appointment Preferences')}>
        <Row
          title={t('Preferred Contact')}
          content={<PreferredContactStatusBadge preferredContact={preferredContact} />}
        />
        <Row title={t('Preferred Time')} content={<PreferredTimeStatusBadge preferredTime={preferredTime} />} />
        <Row title={t('Preferred Days')} content={<PreferredDays preferredDays={preferredDays} />} />
      </Section>

      <Divider />

      <Section heading={t('Insurance')}>
        <Row title={t('Carrier')} content={insuranceCarrier} />
        <Row title={t('Subscriber Name')} content={insuranceSubscriberName} />
        <Row title={t('Subscriber ID')} content={insuranceSubscriberId} />
        <Row title={t('Group Number')} content={insuranceGroupNumber} />
      </Section>

      <Divider />

      <Section heading={t('Attribution')}>
        <Row title={t('Platform')} content={platform} />
        <Row title={t('Source')} content={source} />
        <Row title={t('Campaign')} content={campaignName} />
      </Section>

      <Divider />

      <AdditionalData data={additionalData} />

      <Footer
        locationId={locationId}
        patientContactedAt={patientContactedAt}
        dismissRequestModalProps={dismissRequestModalProps}
        onDismissRequestIntent={onDismissRequestIntent}
        onMessage={onMessage}
        onPhoneCall={onPhoneCall}
        onCompleteRequest={onCompleteRequest}
      />
    </div>
  );
};

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
});
